import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, MapPin, Users, AlertCircle } from 'lucide-react';
import { eventsService } from '../../services/eventsService.js';
import styles from './PublicEventPage.module.css';

function formatDateRange(starts, ends) {
  const dateOpts = { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' };
  const timeOpts = { hour: '2-digit', minute: '2-digit' };
  const s = new Date(starts);
  const e = new Date(ends);
  return `${s.toLocaleDateString('en-GB', dateOpts)}, ${s.toLocaleTimeString('en-GB', timeOpts)} – ${e.toLocaleTimeString('en-GB', timeOpts)}`;
}

function formatPrice(pence) {
  if (pence == null || Number.isNaN(Number(pence))) return null;
  return `£${(Number(pence) / 100).toFixed(2)}`;
}

export default function PublicEventPage() {
  const { shortCode } = useParams();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [regError, setRegError] = useState(null);

  useEffect(() => {
    let active = true;
    async function load() {
      setLoading(true);
      setLoadError(null);
      try {
        const ev = await eventsService.getPublicEvent(shortCode);
        if (!active) return;
        if (!ev) setLoadError('not_found');
        else setEvent(ev);
      } catch (err) {
        if (active) setLoadError(err.message || 'Failed to load event');
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => { active = false; };
  }, [shortCode]);

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setRegError(null);
    try {
      const result = await eventsService.registerForEvent(event.id, { name, email });
      if (result.kind === 'checkout') {
        window.location.href = result.checkout.url;
        return;
      }
      navigate(`/e/${shortCode}/success`);
    } catch (err) {
      setRegError(err.message || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.inner}>
          <div className={styles.loadingWrap}>
            <div className={styles.spinner} aria-hidden="true" />
            <p className={styles.loadingText}>Loading event…</p>
          </div>
        </div>
      </div>
    );
  }

  if (loadError || !event) {
    const isNotFound = loadError === 'not_found';
    return (
      <div className={styles.page}>
        <div className={styles.inner}>
          <div className={styles.stateCard}>
            <div className={`${styles.stateIcon} ${styles.stateIconError}`}>
              <AlertCircle size={28} />
            </div>
            <h1 className={styles.stateTitle}>{isNotFound ? 'Event not found' : 'Could not load event'}</h1>
            <p className={styles.stateBody}>
              {isNotFound
                ? 'This event link may have expired or been removed.'
                : (loadError || 'Please try again in a moment.')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const priceLabel = formatPrice(event.price_pence); // null on free / missing
  const submitLabel = submitting
    ? 'Submitting…'
    : event.is_free
      ? 'Register for free'
      : (priceLabel ? `Pay ${priceLabel}` : 'Get tickets');

  return (
    <div className={styles.page}>
      <div className={styles.inner}>
        <div className={styles.card}>
          <div className={styles.header}>
            <h1 className={styles.title}>{event.title}</h1>
            <div className={styles.meta}>
              {event.starts_at && event.ends_at && (
                <div className={styles.metaRow}>
                  <Calendar size={16} className={styles.metaIcon} />
                  <span>{formatDateRange(event.starts_at, event.ends_at)}</span>
                </div>
              )}
              {event.location && (
                <div className={styles.metaRow}>
                  <MapPin size={16} className={styles.metaIcon} />
                  <span>{event.location}</span>
                </div>
              )}
              <div className={styles.metaRow}>
                <Users size={16} className={styles.metaIcon} />
                <span>{`Capacity: ${event.capacity}`}</span>
              </div>
            </div>
            {event.description && (
              <p className={styles.description}>{event.description}</p>
            )}
            {!event.is_free && priceLabel && (
              <p className={styles.price}>{priceLabel} per ticket</p>
            )}
          </div>
        </div>

        <div className={styles.card}>
          <h2 className={styles.sectionTitle}>
            {event.is_free ? 'Reserve your spot' : 'Get tickets'}
          </h2>

          <form className={styles.form} onSubmit={handleSubmit} noValidate>
            <div className={styles.field}>
              <label htmlFor="reg-name" className={styles.label}>Name</label>
              <input
                id="reg-name"
                className={styles.input}
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                autoComplete="name"
                required
              />
            </div>
            <div className={styles.field}>
              <label htmlFor="reg-email" className={styles.label}>Email</label>
              <input
                id="reg-email"
                className={styles.input}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                required
              />
            </div>

            {regError && (
              <div className={styles.errorBox} role="alert">
                <AlertCircle size={16} className={styles.errorIcon} />
                <p className={styles.errorText}>{regError}</p>
              </div>
            )}

            <button
              type="submit"
              className={styles.confirmBtn}
              disabled={submitting || !name.trim() || !email.trim()}
            >
              {submitLabel}
            </button>
          </form>
        </div>

        <p className={styles.footer}>
          Powered by <a href="/" className={styles.footerBrand}>Vennio</a>
        </p>
      </div>
    </div>
  );
}
